// Wizard registry. Side-effect import: calling registerWizard wires the
// extension dispatch table in assetUploadDispatch. ContentBrowser imports
// this once at mount time.

// Wizard registry. Per-character scope means rigs are owned by the
// Character Creation wizard (not the content browser), so .glb files
// dropped into the browser are unambiguous body animations. No
// disambiguation step needed.
//
// Asset-type → extension map:
//   .wav .mp3 .ogg .m4a  → AudioWizard
//   .json                → FaceAnimWizard
//   .glb                 → BodyAnimWizard

import AudioWizard from './AudioWizard'
import BodyAnimWizard from './BodyAnimWizard'
import FbxAnimWizard from './FbxAnimWizard'
import FaceAnimWizard from './FaceAnimWizard'
import PrefabWizard from './PrefabWizard'
import TextureWizard from './TextureWizard'
import RigWizard from './RigWizard'
import EnvironmentDirectUpload from './EnvironmentDirectUpload'
import GarmentDirectUpload from './GarmentDirectUpload'
import GltfKindStep from './GltfKindStep'
import OgsDirectUpload from './OgsDirectUpload'
import OrlDnaDirectUpload from './OrlDnaDirectUpload'
import BlueprintImportUpload from './BlueprintImportUpload'
import { registerWizard } from '../../../lib/assetUploadDispatch'
import { ORL_DNA_EXTENSION } from '../../../lib/orl/dnaUpload'

registerWizard('audio', {
  component: AudioWizard,
  extensions: ['wav', 'mp3', 'ogg', 'm4a'],
  label: 'Audio',
})

registerWizard('face-anim', {
  component: FaceAnimWizard,
  extensions: ['json'],
  label: 'Face animation',
})

registerWizard('body-anim', {
  component: BodyAnimWizard,
  extensions: ['glb'],
  label: 'Body animation',
})

// ENG-661: source animation FBX → server-side conversion to body GLB.
registerWizard('fbx-anim', {
  component: FbxAnimWizard,
  extensions: ['fbx'],
  label: 'Animation FBX (convert)',
})

// ENG-1108. Shares `glb`/`gltf` with body-anim (and, by kind, the rig): the
// EXTENSION decides nothing here, so buildUploadQueue content-sniffs and routes
// by what the bytes are (gltfKind.js). Registering the extensions keeps the
// file-picker's accept list honest; the sniff is what actually picks.
registerWizard('garment', {
  component: GarmentDirectUpload,
  extensions: ['glb', 'gltf'],
  label: 'Garment',
})

// Every .glb / .gltf drop lands HERE first — the import-type dialog — and is
// routed on by what the user confirms (buildUploadQueue's 'gltf-kind' key). It
// claims no extensions: it is not reached by dispatch, it IS the dispatch for
// glTF. Registering extensions would put it in the accept list twice over and
// make candidatesFor return a key that is a question rather than an uploader.
registerWizard('gltf-kind', {
  component: GltfKindStep,
  extensions: [],
  label: 'glTF (choose type)',
})

registerWizard('texture', {
  component: TextureWizard,
  extensions: ['png', 'jpg', 'jpeg', 'webp', 'tga'],
  label: 'Texture',
})

// PrefabWizard is launched from the "+ New Prefab" toolbar button, not from
// a file drop. Registered here so the ContentBrowser can look it up by key.
registerWizard('prefab', {
  component: PrefabWizard,
  extensions: [],
  label: 'Prefab',
})

// RigWizard replaces the active character's rig. Launched from the
// "Replace Rig" toolbar button — never from a file drop (destructive).
registerWizard('rig-replace', {
  component: RigWizard,
  extensions: [],
  label: 'Replace rig',
})

// ENG-529: gsplat environment files (.ply, .splat, .spz, .ksplat) upload
// directly with no form — see EnvironmentDirectUpload.jsx. Edits to name /
// default_pose / folder happen in the inspector after the row exists.
registerWizard('environment-direct', {
  component: EnvironmentDirectUpload,
  extensions: ['ply', 'splat', 'spz', 'ksplat'],
  label: 'Environment',
})

// ENG-921: the 'gss-hair-pack' wizard (.gsh drag-drop → convorcher's gss_hair
// config) is gone with its backend — ENG-579 removed the handlers and migration
// 00028 dropped the column and table, so the upload could only 404. A bundle's
// hair.gsh now ships inside the character's AAM /ogs bundle instead.

// OGS inference bundle — multi-file upload launched from "New → OGS bundle"
// (no extensions: .onnx/.json/.bin/.npy collide with other kinds, so it's never
// drop-dispatched). See OgsDirectUpload.jsx.
registerWizard('ogs', {
  component: OgsDirectUpload,
  extensions: [],
  label: 'OGS bundle',
})

// A MetaHuman head.dna converts in the browser and uploads as ONE
// orl_pack.bin into this character's /ogs collection — see
// OrlDnaDirectUpload.jsx. `.dna` is claimed by nothing else, so it is
// drop-dispatched with no disambiguation step.
// A blueprint card exported from any character re-imports as a NEW card here.
//
// It claims 'json' alongside the face-clip wizard, which is safe ONLY because
// buildUploadQueue content-sniffs a multi-candidate JSON for the
// `aosBlueprintClip` tag. Without that sniff both wizards would claim the
// extension, candidatesFor would return two keys, and every dropped .json would
// route to a wizard key with no component — warned and silently discarded.
registerWizard('blueprint-import', {
  component: BlueprintImportUpload,
  extensions: ['json'],
  label: 'Blueprint (import as new card)',
})

registerWizard('orl-dna', {
  component: OrlDnaDirectUpload,
  extensions: [ORL_DNA_EXTENSION],
  label: 'MetaHuman DNA (convert)',
})

export { GltfKindStep, BlueprintImportUpload, AudioWizard, BodyAnimWizard, FbxAnimWizard, FaceAnimWizard, PrefabWizard, TextureWizard, RigWizard, EnvironmentDirectUpload, OgsDirectUpload, OrlDnaDirectUpload }
